import React from 'react';

/**
 * MiniCalendar - Compact timeline view for ride request cards
 * Shows a time window with existing rides and the pending ride request
 */
const MiniCalendar = ({ events, timeWindow }) => {
  if (!events || !timeWindow) {
    return <div className="text-gray-500 text-sm">Loading calendar...</div>;
  }

  // Parse time window
  const windowStart = new Date(timeWindow.start);
  const windowEnd = new Date(timeWindow.end);

  // Calculate total duration in hours
  const totalMinutes = (windowEnd - windowStart) / (1000 * 60);
  const totalHours = totalMinutes / 60;

  // Determine appropriate height based on window size
  const minHeight = 400;
  const maxHeight = 600;
  const calculatedHeight = Math.min(Math.max(totalHours * 40, minHeight), maxHeight);

  // Convert time to minutes since window start
  const timeToMinutesFromStart = (timeStr) => {
    const time = new Date(timeStr);
    const diffMs = time - windowStart;
    return diffMs / (1000 * 60);
  };

  // Calculate position and height for an event
  const getEventStyle = (event) => {
    const startMinutes = timeToMinutesFromStart(event.start);
    const endMinutes = timeToMinutesFromStart(event.end);
    const durationMinutes = endMinutes - startMinutes;

    const pixelsPerMinute = calculatedHeight / totalMinutes;

    const top = startMinutes * pixelsPerMinute;
    const height = Math.max(durationMinutes * pixelsPerMinute, 20); // Min 20px height

    return {
      top: `${top}px`,
      height: `${height}px`
    };
  };

  // Format time for display
  const formatTime = (timeStr) => {
    const date = new Date(timeStr);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Get event color classes
  const getEventColor = (type, isPending) => {
    if (isPending) {
      return 'bg-green-100 border-green-500';
    }

    const colors = {
      'ride': 'bg-green-100 border-green-600',
      'pre-buffer': 'bg-yellow-100 border-yellow-500',
      'post-buffer': 'bg-yellow-100 border-yellow-500',
      'available': 'bg-blue-50 border-blue-300',
      'blackout': 'bg-purple-100 border-purple-600'
    };
    return colors[type] || 'bg-gray-100 border-gray-400';
  };

  // Get event text color
  const getEventTextColor = (type, isPending) => {
    if (isPending) {
      return 'text-green-800';
    }

    const textColors = {
      'ride': 'text-green-800',
      'pre-buffer': 'text-yellow-800',
      'post-buffer': 'text-yellow-800',
      'available': 'text-blue-700',
      'blackout': 'text-purple-800'
    };
    return textColors[type] || 'text-gray-800';
  };

  // Get z-index for layering
  const getEventZIndex = (type, isPending) => {
    if (isPending) return 'z-40'; // Pending ride on top

    const zIndexes = {
      'available': 'z-0',
      'pre-buffer': 'z-10',
      'post-buffer': 'z-10',
      'ride': 'z-20',
      'blackout': 'z-30'
    };
    return zIndexes[type] || 'z-0';
  };

  // Generate hour markers
  const generateHourMarkers = () => {
    const markers = [];
    let current = new Date(windowStart);

    // Round to next hour
    current.setMinutes(0, 0, 0);
    if (current < windowStart) {
      current.setHours(current.getHours() + 1);
    }

    while (current <= windowEnd) {
      markers.push(new Date(current));
      current.setHours(current.getHours() + 1);
    }

    return markers;
  };

  const hourMarkers = generateHourMarkers();

  return (
    <div className="bg-white rounded border border-gray-200">
      {/* Header */}
      <div className="border-b p-3 bg-gray-50">
        <h4 className="text-sm font-semibold text-gray-700">
          Schedule Preview: {formatTime(timeWindow.start)} - {formatTime(timeWindow.end)}
        </h4>
      </div>

      {/* Timeline */}
      <div className="p-4" style={{ height: `${calculatedHeight + 50}px`, overflowY: 'auto' }}>
        <div className="relative" style={{ height: `${calculatedHeight}px` }}>
          {/* Hour markers */}
          {hourMarkers.map((marker, idx) => {
            const minutesFromStart = (marker - windowStart) / (1000 * 60);
            const pixelsPerMinute = calculatedHeight / totalMinutes;
            const top = minutesFromStart * pixelsPerMinute;

            return (
              <div
                key={idx}
                className="absolute left-0 right-0 border-t border-gray-200"
                style={{ top: `${top}px` }}
              >
                <span className="text-xs text-gray-500 bg-white px-1">
                  {formatTime(marker.toISOString())}
                </span>
              </div>
            );
          })}

          {/* Events */}
          <div className="absolute top-0 left-16 right-0 bottom-0">
            {events.map((event, idx) => {
              const style = getEventStyle(event);
              const eventHeight = parseInt(style.height);
              const isPending = event.isPending || false;

              // Determine what to show based on height
              const showDetails = eventHeight >= 60;
              const showTitle = eventHeight >= 30;

              // Build tooltip
              const tooltipText = `${event.title}\n${formatTime(event.start)} - ${formatTime(event.end)}${event.pickup ? '\n📍 ' + event.pickup : ''}${event.dropoff ? '\n🎯 ' + event.dropoff : ''}${event.price ? '\n💰 $' + event.price.toFixed(2) : ''}`;

              return (
                <div
                  key={idx}
                  className={`
                    absolute left-0 right-2
                    ${getEventColor(event.type, isPending)}
                    ${getEventTextColor(event.type, isPending)}
                    ${getEventZIndex(event.type, isPending)}
                    border-l-4 rounded shadow-sm
                    ${isPending ? 'border-dashed border-2' : ''}
                    overflow-hidden p-2
                    hover:shadow-md transition-shadow cursor-pointer
                  `}
                  style={style}
                  title={tooltipText}
                >
                  {/* Pending badge */}
                  {isPending && showTitle && (
                    <div className="bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded mb-1 inline-block">
                      PENDING ACCEPTANCE
                    </div>
                  )}

                  {showTitle && (
                    <div className="text-xs font-semibold truncate">
                      {event.title}
                    </div>
                  )}

                  {showDetails && (
                    <>
                      <div className="text-xs text-gray-600 truncate">
                        {formatTime(event.start)} - {formatTime(event.end)}
                      </div>
                      {event.pickup && (
                        <div className="text-xs truncate mt-1">
                          📍 {event.pickup.split(',')[0]}
                        </div>
                      )}
                      {event.price && (
                        <div className="text-xs font-semibold mt-1">
                          ${event.price.toFixed(2)}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="border-t p-3 bg-gray-50">
        <div className="flex flex-wrap gap-3 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-green-100 border-2 border-dashed border-green-500 rounded"></div>
            <span>Pending Ride</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-green-100 border-l-4 border-green-600 rounded"></div>
            <span>Scheduled Ride</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-yellow-100 border-l-4 border-yellow-500 rounded"></div>
            <span>Buffer Time</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MiniCalendar;
